import React from 'react';
import { CfoMetrics } from './CfoMetrics';
import { TaskOverview } from '../ceo/TaskOverview';
import { useStore } from '../../../store/useStore';
import { useTasks } from '../../../hooks/database/useTasks';
import { motion } from 'framer-motion';
import { Card } from '../../ui/Card';
import { ListTodo, CheckCircle, Clock, Building2, DollarSign, FileText, TrendingUp, AlertTriangle } from 'lucide-react';
import { format } from 'date-fns';
import { getDepartmentLabel } from '../../../utils/departments';

export const CfoDashboard: React.FC = () => {
  const { currentUser } = useStore();
  const { tasks, isLoading } = useTasks();
  
  // Get tasks assigned by current user
  const assignedTasks = tasks.filter(task => task.assignedBy === currentUser?.id);

  // Get all company tasks for monitoring
  const allTasks = [...tasks].sort((a, b) => 
    new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
  ).slice(0, 5); // Show 5 most recent tasks

  return (
    <div className="max-w-7xl mx-auto space-y-8">
      <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
        <div className="flex items-center gap-6 mb-6">
          <img
            src={currentUser?.avatar}
            alt={currentUser?.name}
            className="w-16 h-16 rounded-full ring-4 ring-emerald-500 ring-offset-4"
          />
          <div>
            <h1 className="text-3xl font-bold text-gray-900">CFO Dashboard</h1>
            <p className="text-gray-600">Welcome back, {currentUser?.name}</p>
          </div>
        </div>

        {/* Financial Overview Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
          <Card className="p-4 bg-emerald-50">
            <div className="flex items-center gap-2 mb-2">
              <DollarSign className="w-4 h-4 text-emerald-600" />
              <h3 className="text-sm font-medium text-emerald-700">Revenue</h3>
            </div>
            <p className="text-2xl font-bold text-emerald-900">$1.2M</p>
            <p className="text-sm text-emerald-600 mt-1">+15% from last month</p>
          </Card>

          <Card className="p-4 bg-blue-50">
            <div className="flex items-center gap-2 mb-2">
              <FileText className="w-4 h-4 text-blue-600" />
              <h3 className="text-sm font-medium text-blue-700">Accounts</h3>
            </div>
            <p className="text-2xl font-bold text-blue-900">2,145</p>
            <p className="text-sm text-blue-600 mt-1">Active accounts</p>
          </Card>

          <Card className="p-4 bg-purple-50">
            <div className="flex items-center gap-2 mb-2">
              <TrendingUp className="w-4 h-4 text-purple-600" />
              <h3 className="text-sm font-medium text-purple-700">Growth</h3>
            </div>
            <p className="text-2xl font-bold text-purple-900">23.5%</p>
            <p className="text-sm text-purple-600 mt-1">Year over year</p>
          </Card>

          <Card className="p-4 bg-amber-50">
            <div className="flex items-center gap-2 mb-2">
              <AlertTriangle className="w-4 h-4 text-amber-600" />
              <h3 className="text-sm font-medium text-amber-700">Pending</h3>
            </div>
            <p className="text-2xl font-bold text-amber-900">$245K</p>
            <p className="text-sm text-amber-600 mt-1">Outstanding invoices</p>
          </Card>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="space-y-6"
        >
          <CfoMetrics />
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card className="p-4 sm:p-6">
              <div className="flex items-center gap-3 mb-6">
                <div className="p-2 bg-purple-100 rounded-lg">
                  <Building2 className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold">Company Task Monitor</h2>
                  <p className="text-sm text-gray-600">Recent tasks across all departments</p>
                </div>
              </div>

              <div className="space-y-4">
                {isLoading ? (
                  <div className="text-center py-8 text-gray-500">Loading tasks...</div>
                ) : allTasks.length > 0 ? (
                  allTasks.map(task => (
                    <motion.div
                      key={task.id}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="p-3 sm:p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                    >
                      <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-2 sm:gap-4">
                        <div className="min-w-0">
                          <h3 className="font-medium text-gray-900 truncate">{task.title}</h3>
                          <p className="text-sm text-gray-600 mt-1 line-clamp-2">{task.description}</p>
                          <div className="flex flex-wrap items-center gap-x-2 gap-y-1 mt-2 text-xs text-gray-500">
                            <span className="inline-flex items-center">
                              From: {task.assignedBy}
                            </span>
                            <span className="hidden sm:inline">•</span>
                            <span className="inline-flex items-center">
                              To: {task.assignedTo}
                            </span>
                            <span className="hidden sm:inline">•</span>
                            <span className="inline-flex items-center">
                              {getDepartmentLabel(task.department)}
                            </span>
                            <span className="hidden sm:inline">•</span>
                            <span className="inline-flex items-center">
                              Due: {format(new Date(task.dueDate), 'MMM d, h:mm a')}
                            </span>
                          </div>
                        </div>
                        <div className={`px-2 py-1 text-xs font-medium rounded-full shrink-0 ${
                          task.status === 'COMPLETED' 
                            ? 'bg-emerald-100 text-emerald-700'
                            : task.status === 'IN_PROGRESS'
                            ? 'bg-blue-100 text-blue-700'
                            : 'bg-gray-100 text-gray-700'
                        }`}>
                          {task.status}
                        </div>
                      </div>

                      {task.subtasks && task.subtasks.length > 0 && (
                        <div className="mt-4 pl-4 border-l-2 border-gray-200 space-y-3">
                          {task.subtasks.map((subtask) => (
                            <div key={subtask.id} className="flex items-center justify-between">
                              <div className="flex items-center gap-2 min-w-0">
                                {subtask.acknowledged ? (
                                  <CheckCircle className="w-4 h-4 shrink-0 text-emerald-500" />
                                ) : (
                                  <Clock className="w-4 h-4 shrink-0 text-gray-400" />
                                )}
                                <span className="text-sm text-gray-700 truncate">{subtask.title}</span>
                              </div>
                              <span className={`ml-2 text-xs font-medium px-2 py-1 rounded-full shrink-0 ${
                                subtask.completedAt
                                  ? 'bg-emerald-100 text-emerald-700'
                                  : subtask.acknowledged
                                  ? 'bg-blue-100 text-blue-700'
                                  : 'bg-gray-100 text-gray-700'
                              }`}>
                                {subtask.completedAt 
                                  ? 'Completed' 
                                  : subtask.acknowledged 
                                  ? 'In Progress'
                                  : 'Pending'}
                              </span>
                            </div>
                          ))}
                        </div>
                      )}
                    </motion.div>
                  ))
                ) : (
                  <div className="text-center py-8 text-gray-500">No tasks found</div>
                )}
              </div>
            </Card>
            
            <Card className="p-4 sm:p-6">
              <div className="flex items-center gap-3 mb-6">
                <div className="p-2 bg-emerald-100 rounded-lg">
                  <ListTodo className="w-5 h-5 text-emerald-600" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold">Task Details</h2>
                  <p className="text-sm text-gray-600">Track task and subtask progress</p>
                </div>
              </div>

              <div className="space-y-4">
                {isLoading ? (
                  <div className="text-center py-8 text-gray-500">Loading tasks...</div>
                ) : assignedTasks.length > 0 ? (
                  assignedTasks.map((task) => (
                    <motion.div
                      key={task.id}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="p-3 sm:p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                    >
                      <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-2 sm:gap-4">
                        <div className="min-w-0">
                          <h3 className="font-medium text-gray-900 truncate">{task.title}</h3>
                          <p className="text-sm text-gray-600 mt-1 line-clamp-2">{task.description}</p>
                          <div className="flex flex-wrap items-center gap-x-2 gap-y-1 mt-2 text-xs text-gray-500">
                            <span className="inline-flex items-center">
                              Assigned to: {task.assignedTo}
                            </span>
                            <span className="hidden sm:inline">•</span>
                            <span className="inline-flex items-center">
                              {getDepartmentLabel(task.department)}
                            </span>
                            <span className="hidden sm:inline">•</span>
                            <span className="inline-flex items-center">
                              Due: {format(new Date(task.dueDate), 'MMM d, h:mm a')}
                            </span>
                          </div>
                        </div>
                        <div className={`px-2 py-1 text-xs font-medium rounded-full shrink-0 ${
                          task.status === 'COMPLETED' 
                            ? 'bg-emerald-100 text-emerald-700'
                            : task.status === 'IN_PROGRESS'
                            ? 'bg-blue-100 text-blue-700'
                            : 'bg-gray-100 text-gray-700'
                        }`}>
                          {task.status}
                        </div>
                      </div>

                      {task.subtasks && task.subtasks.length > 0 && (
                        <div className="mt-4 pl-4 border-l-2 border-gray-200 space-y-3">
                          {task.subtasks.map((subtask) => (
                            <div key={subtask.id} className="flex items-center justify-between">
                              <div className="flex items-center gap-2 min-w-0">
                                {subtask.acknowledged ? (
                                  <CheckCircle className="w-4 h-4 shrink-0 text-emerald-500" />
                                ) : (
                                  <Clock className="w-4 h-4 shrink-0 text-gray-400" />
                                )}
                                <span className="text-sm text-gray-700 truncate">{subtask.title}</span>
                              </div>
                              <span className={`ml-2 text-xs font-medium px-2 py-1 rounded-full shrink-0 ${
                                subtask.completedAt
                                  ? 'bg-emerald-100 text-emerald-700'
                                  : subtask.acknowledged
                                  ? 'bg-blue-100 text-blue-700'
                                  : 'bg-gray-100 text-gray-700'
                              }`}>
                                {subtask.completedAt 
                                  ? 'Completed' 
                                  : subtask.acknowledged 
                                  ? 'In Progress'
                                  : 'Pending'}
                              </span>
                            </div>
                          ))}
                        </div>
                      )}
                    </motion.div>
                  ))
                ) : (
                  <div className="text-center py-8 text-gray-500">No tasks assigned yet</div>
                )}
              </div>
            </Card>
          </div>
        </motion.div>
      </div>
    </div>
  );
};